import styles from './styles.module.scss';
import { DailySunlightResult } from '@/types/sunlight';
import { calculateSunlightScore } from '@/services/score/sunlight';
import { useDailySunlightStore } from '@/store/dailySunlightsStore';
import { useSpacePlanningStore } from '@/store/spacePlanningStore';

interface GridPointDataProps {
	data: DailySunlightResult;
}

export const GridPointData = (props: GridPointDataProps) => {
	const data = props.data;
	const sunlightResultsRooms = useDailySunlightStore((state) => state.sunlightResultsRooms);
	const spaces = useSpacePlanningStore((state) => state.spaces);

	// Get room display name from space planning data using unitId
	const getRoomDisplayName = () => {
		if (!data.unitId) return null;

		// Find the room data matching this unit ID
		for (const spaceData of Object.values(spaces)) {
			const room = spaceData.rooms.find((r) => r.id === data.unitId);
			if (room) return room.spaceDescription;
		}

		return null;
	};

	const roomDisplayName = getRoomDisplayName();

	// Check if this is facade data (only has averageDailySunlightHours and roomName)
	const isFacadeData = data.roomName === 'Facade';

	// If it's facade data, show simplified tooltip
	if (isFacadeData) {
		const hours = data.averageDailySunlightHours ?? 0;
		const hoursColor = hours > 6 ? '#00FF00' : hours > 4 ? '#FFD700' : '#FF6B6B';

		return (
			<div>
				<div className={styles.header}>
					<span className={styles.icon}>🏢</span>
					<span className={styles.title}>Facade Sunlight</span>
				</div>

				<div className={styles.primary}>
					<div className={styles.mainMetric} style={{ color: hoursColor }}>
						{hours.toFixed(1)} hrs/day
					</div>
					<div className={styles.consistency}>
						<span className={styles.label}>Average Daily Sunlight</span>
					</div>
				</div>
			</div>
		);
	}

	// Calculate room score if roomName is available
	const roomScore = data.roomName && sunlightResultsRooms
		? calculateSunlightScore(
			sunlightResultsRooms.filter((r) => r.roomName === data.roomName)
		)
		: null;

	// Full floor sunlight analysis display
	const formatDate = (dateStr: string | null | undefined) => {
		if (!dateStr) return 'N/A';
		const date = new Date(dateStr);
		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
		});
	};

	const consistency = data.consistencyScore ?? 0;
	const consistencyColor =
		consistency > 0.7 ? '#00FF00' : consistency > 0.4 ? '#FFD700' : '#FF6B6B';
	const consistencyLevel = consistency > 0.7 ? '●●●' : consistency > 0.4 ? '●●○' : '●○○';

	const winterAvg = data.winterMonthsAverage ?? 0;
	const winterColor = winterAvg > 4 ? '#90EE90' : winterAvg > 2 ? '#FFD700' : '#FF6B6B';

	const daylightAutonomy = data.daylightAutonomyPercent ?? 0;
	const autonomyColor =
		daylightAutonomy > 70 ? '#00FF00' : daylightAutonomy > 50 ? '#FFD700' : '#FF6B6B';

	const glareRisk = data.glareRiskHours ?? 0;
	const glareColor = glareRisk < 1 ? '#00FF00' : glareRisk < 2 ? '#FFD700' : '#FF6B6B';

	const dirBalance = data.directionalBalance ?? 0;
	const balanceColor = dirBalance > 0.7 ? '#00FF00' : dirBalance > 0.4 ? '#FFD700' : '#FF6B6B';

	const viableDays = data.minViableDaysCount ?? 0;
	const totalDays = data.totalDaysAnalyzed ?? 1;
	const viablePercent = (viableDays / totalDays) * 100;
	const viableColor = viablePercent > 80 ? '#00FF00' : viablePercent > 50 ? '#FFD700' : '#FF6B6B';

	const getScoreColor = (score: number) => {
		if (score >= 7) return '#00FF00';
		if (score >= 5) return '#90EE90';
		if (score >= 3) return '#FFD700';
		return '#FF6B6B';
	};

	return (
		<div>
			<div className={styles.header}>
				<span className={styles.icon}>☀️</span>
				<span className={styles.title}>{roomDisplayName || 'Sunlight Analysis'}</span>
			</div>

			{roomScore && (
				<div className={styles.section} style={{ borderBottom: '1px solid rgba(255,255,255,0.2)', paddingBottom: '8px' }}>
					<div className={styles.sectionTitle}>Room Score</div>
					<div className={styles.row}>
						<span className={styles.label}>Overall:</span>
						<span className={styles.value} style={{ color: getScoreColor(roomScore.totalScore), fontSize: '1.2em', fontWeight: 'bold' }}>
							{roomScore.totalScore.toFixed(1)}/10
						</span>
						<span className={styles.date}>({roomScore.pointsAnalyzed} cells)</span>
					</div>
				</div>
			)}

			<div className={styles.primary}>
				<div className={styles.mainMetric}>
					{(data.averageDailySunlightHours ?? 0).toFixed(1)} hrs/day
				</div>
				<div className={styles.consistency}>
					<span className={styles.label}>Consistency:</span>
					<span className={styles.value} style={{ color: consistencyColor }}>
						{(consistency * 100).toFixed(0)}% {consistencyLevel}
					</span>
				</div>
			</div>

			<div className={styles.section}>
				<div className={styles.sectionTitle}>Seasonal Analysis</div>
				<div className={styles.row}>
					<span className={styles.label}>Winter (Dec-Feb):</span>
					<span className={styles.value} style={{ color: winterColor }}>
						{winterAvg.toFixed(1)}h
					</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Spring (Mar-May):</span>
					<span className={styles.value} style={{ color: '#98FB98' }}>
						{(data.springMonthsAverage ?? 0).toFixed(1)}h
					</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Summer (Jun-Aug):</span>
					<span className={styles.value} style={{ color: '#FFA500' }}>
						{(data.summerMonthsAverage ?? 0).toFixed(1)}h
					</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Fall (Sep-Nov):</span>
					<span className={styles.value} style={{ color: '#DEB887' }}>
						{(data.fallMonthsAverage ?? 0).toFixed(1)}h
					</span>
				</div>
			</div>

			<div className={styles.section}>
				<div className={styles.sectionTitle}>Daily Pattern</div>
				<div className={styles.row}>
					<span className={styles.label}>Peak Hour:</span>
					<span className={styles.value} style={{ color: '#FFD700' }}>
						{data.peakSunlightHour ?? 'N/A'}
					</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>AM/PM Split:</span>
					<span className={styles.value} style={{ color: '#90EE90' }}>
						{(data.morningHoursPercent ?? 0).toFixed(0)}% /{' '}
						{(data.afternoonHoursPercent ?? 0).toFixed(0)}%
					</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Midday (10am-2pm):</span>
					<span className={styles.value} style={{ color: '#FFD700' }}>
						{(data.midDayHoursAverage ?? 0).toFixed(1)}h
					</span>
				</div>
			</div>

			<div className={styles.section}>
				<div className={styles.sectionTitle}>Daylighting & Direction</div>
				<div className={styles.row}>
					<span className={styles.label}>Work Hours (8am-6pm):</span>
					<span className={styles.value} style={{ color: autonomyColor }}>
						{daylightAutonomy.toFixed(1)}%
					</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Glare Risk (&lt;15°):</span>
					<span className={styles.value} style={{ color: glareColor }}>
						{glareRisk.toFixed(1)}h/day
					</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Primary Direction:</span>
					<span className={styles.value} style={{ color: '#87CEEB' }}>
						{data.primarySunDirection ?? 'N/A'}
					</span>
					<span className={styles.label} style={{ marginLeft: '10px' }}>
						Balance:
					</span>
					<span className={styles.value} style={{ color: balanceColor }}>
						{(dirBalance * 100).toFixed(0)}%
					</span>
				</div>
			</div>

			<div className={styles.section}>
				<div className={styles.sectionTitle}>Range</div>
				<div className={styles.row}>
					<span className={styles.label}>Best:</span>
					<span className={styles.value} style={{ color: '#90EE90' }}>
						{(data.longestSunlightHours ?? 0).toFixed(1)}h
					</span>
					<span className={styles.date}>{formatDate(data.longestSunlightDate)}</span>
					<span className={styles.label} style={{ marginLeft: '15px' }}>
						Worst:
					</span>
					<span className={styles.value} style={{ color: '#FF6B6B' }}>
						{(data.shortestSunlightHours ?? 0).toFixed(1)}h
					</span>
					<span className={styles.date}>{formatDate(data.shortestSunlightDate)}</span>
				</div>
			</div>

			<div className={styles.section}>
				<div className={styles.sectionTitle}>Viability</div>
				<div className={styles.row}>
					<span className={styles.label}>Good (&gt;4h):</span>
					<span className={styles.value} style={{ color: viableColor }}>
						{viableDays}/{totalDays}
					</span>
					<span className={styles.date}>({viablePercent.toFixed(0)}%)</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Sunny Days:</span>
					<span className={styles.value} style={{ color: '#90EE90' }}>
						{data.daysWithSunlight ?? 0}/{totalDays}
					</span>
					<span className={styles.label} style={{ marginLeft: '15px' }}>
						No Sun:
					</span>
					<span
						className={styles.value}
						style={{ color: (data.zeroDaysCount ?? 0) === 0 ? '#00FF00' : '#FF6B6B' }}
					>
						{data.zeroDaysCount ?? 0} days
					</span>
				</div>
			</div>

			<div className={styles.footer}>
				Analysis: {formatDate(data.firstSunlightDate)} - {formatDate(data.lastSunlightDate)}
			</div>
		</div>
	);
};
